import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ChartModule } from 'primeng/chart';
import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { TranslocoService } from '@jsverse/transloco';
import { combineLatest } from 'rxjs';
import { DecimalPipe } from '@angular/common';

@Component({
  selector: 'app-consumption-and-costs-doughnut-chart',
  standalone: true,
  imports: [ChartModule],
  providers: [DecimalPipe],
  templateUrl: './consumption-and-costs-doughnut-chart.component.html',
  styleUrl: './consumption-and-costs-doughnut-chart.component.css'
})
export class ConsumptionAndCostsDoughnutChartComponent implements OnInit, OnChanges {
  data: any;
  options: any;

  // Dummy data
  @Input() showDummyData: boolean = false;
  dummylabels: string[] = ['F1', 'F2', 'F3'];
  dummyshowData: number[] = [327.28, 1233, 1233];
  dummytotalConsumption: number = 2793.28;

  @Input() labels: string[] = ['F1', 'F2', 'F3'];
  @Input() showData: number[] = [327.28, 1233, 1233];
  @Input() totalConsumption: number = 2793.28;

  constructor(private translocoService: TranslocoService, private decimalPipe: DecimalPipe) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.updateChart();
  }

  ngOnInit(): void {
    this.updateChart();
  }

  updateChart(): void {
    if (this.showDummyData) {
      this.labels = this.dummylabels;
      this.showData = this.dummyshowData;
      this.totalConsumption = this.dummytotalConsumption;
    }

    Chart.register(ChartDataLabels);

    const centerTextPlugin = {
      id: 'centerText',
      beforeDraw(chart: any) {
        const pluginOptions = chart.config.options.plugins?.centerText;
        if (!pluginOptions || pluginOptions.enabled === false) {
          return;
        }

        const ctx = chart.ctx;

        const { totalConsumption = 0 } = pluginOptions;
        const { top, bottom, left, right } = chart.chartArea;
        const centerX = (left + right) / 2;
        const centerY = (top + bottom) / 2;

        const text = (totalConsumption == 0) ? '' : `${totalConsumption}\nkWh`;

        ctx.save();
        ctx.font = 'bold 20px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        text.split('\n').forEach((line, index) => {
          ctx.fillText(line, centerX, centerY + index * 20);
        });

        ctx.restore();
      },
    };
    Chart.register(centerTextPlugin);

    this.data = {
      labels: this.labels,
      datasets: [
        {
          data: this.showData,
          backgroundColor: ['#769257', '#A9BE92', '#BDCAAC'],
          hoverBackgroundColor: ['#769257', '#A9BE92', '#BDCAAC'],
          borderWidth: 5,
          borderRadius: 6,
          spacing: 2,
        },
      ],
    };

    this.options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            usePointStyle: true,
            font: {
              size: 12,
            },
          },
          tooltip: {
            callbacks: {
              label: (context: any) => {
                const index = context.dataIndex;
                let label = context.dataset.label || '';
                let value = context.raw || 0;
                return `${label}: ${this.decimalPipe.transform(value, '1.2-2') || value}`;
              }
            }
          },
        },
        datalabels: {
          color: '#2B2B2B',
          font: {
            weight: 'normal',
            size: 14,
            family: 'Gilroy',
          },
          padding: {
            top: 9,
            bottom: 9,
            left: 9,
            right: 9,
          },
          align: 'end',
          anchor: 'center',
          formatter: (value: any, ctx: any) => {
            const dataArr = ctx.chart.data.datasets[0].data;
            const total = dataArr.reduce((sum: any, v: any) => sum + v, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            const label = ctx.chart.data.labels[ctx.dataIndex] || "";
            const shortLabel = label.slice(0, 2);
            return `  ${shortLabel} ${percentage}%  `;
          },
        },
        centerText: {
          enabled: true,
          totalConsumption: this.decimalPipe.transform(this.totalConsumption, '1.0-0') || this.totalConsumption
        }
      },
      layout: {
        padding: 30,
      },
      cutout: '70%',
    };
  }
}
